import { useTranslation } from "@/i18n";
import { MSG } from "@ease/shared/i18n/message-keys";

/** F.3: Platform Analytics Dashboard — cross-tenant ClickHouse metrics */
export default function AdminAnalyticsPage() {
  const { t } = useTranslation();
  const kpis = [
    { label: t(MSG.admin.analytics.kpis.totalMrr), value: "€0", change: "+0%", detail: t(MSG.admin.analytics.kpis.payingTenants) },
    { label: t(MSG.admin.analytics.kpis.totalContacts), value: "0", change: "+0", detail: t(MSG.admin.analytics.kpis.acrossAllTenants) },
    { label: t(MSG.admin.analytics.kpis.totalEvents), value: "0", change: "+0", detail: t(MSG.admin.analytics.kpis.last30Days) },
    { label: t(MSG.admin.analytics.kpis.activeTenants), value: "0", change: "+0", detail: t(MSG.admin.analytics.kpis.activeLast7Days) },
    { label: t(MSG.admin.analytics.kpis.churnRate), value: "0%", change: "+0%", detail: t(MSG.admin.analytics.kpis.monthly) },
    { label: t(MSG.admin.analytics.kpis.avgMrrPerTenant), value: "€0", change: "+0%", detail: t(MSG.admin.analytics.kpis.payingTenantsOnly) },
  ];

  const topTenants = [
    { name: "—", metric: "revenue", value: "€0" },
    { name: "—", metric: "contacts", value: "0" },
    { name: "—", metric: "events", value: "0" },
  ];

  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-bold text-white">{t(MSG.admin.analytics.title)}</h1>
      <p className="text-muted-foreground">{t(MSG.admin.analytics.description)}</p>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {kpis.map((kpi) => (
          <div key={kpi.label} className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-4">
            <p className="text-xs text-muted-foreground">{kpi.label}</p>
            <p className="mt-1 text-2xl font-bold text-white">{kpi.value}</p>
            <div className="mt-1 flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                {kpi.change} {t(MSG.admin.analytics.changeVsPrevMonth)}
              </span>
              <span className="text-xs text-muted-foreground">{kpi.detail}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-6">
        <h2 className="mb-4 font-semibold text-white">{t(MSG.admin.analytics.growth.title)}</h2>
        <div className="grid gap-6 md:grid-cols-2">
          <div>
            <p className="mb-2 text-sm text-muted-foreground">{t(MSG.admin.analytics.growth.newTenantsPerMonth)}</p>
            <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
              {t(MSG.admin.analytics.growth.chartRendersProvisioned)}
            </div>
          </div>
          <div>
            <p className="mb-2 text-sm text-muted-foreground">{t(MSG.admin.analytics.growth.mrrGrowth)}</p>
            <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
              {t(MSG.admin.analytics.growth.chartRendersSubscriptions)}
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-6">
        <h2 className="mb-4 font-semibold text-white">{t(MSG.admin.analytics.topTenants.title)}</h2>
        <div className="grid gap-4 md:grid-cols-3">
          {[
            { title: t(MSG.admin.analytics.topTenants.byRevenue), items: topTenants.filter((tenant) => tenant.metric === "revenue") },
            { title: t(MSG.admin.analytics.topTenants.byContacts), items: topTenants.filter((tenant) => tenant.metric === "contacts") },
            { title: t(MSG.admin.analytics.topTenants.byActivity), items: topTenants.filter((tenant) => tenant.metric === "events") },
          ].map((col) => (
            <div key={col.title}>
              <p className="mb-2 text-sm text-muted-foreground">{col.title}</p>
              {col.items.length === 0 ? (
                <p className="text-xs text-muted-foreground">{t(MSG.admin.analytics.topTenants.noDataYet)}</p>
              ) : (
                col.items.map((tenant, index) => (
                  <div key={index} className="flex items-center justify-between py-1">
                    <span className="text-sm text-muted-foreground">{tenant.name}</span>
                    <span className="text-sm font-medium text-white">{tenant.value}</span>
                  </div>
                ))
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-6">
        <h2 className="mb-4 font-semibold text-white">{t(MSG.admin.analytics.platformHealth.title)}</h2>
        <div className="grid gap-4 md:grid-cols-4">
          {[
            { label: t(MSG.admin.analytics.platformHealth.workerErrorRate), value: "0%", status: "healthy" },
            { label: t(MSG.admin.analytics.platformHealth.queueDepth), value: "0", status: "healthy" },
            { label: t(MSG.admin.analytics.platformHealth.dbConnections), value: "0/200", status: "healthy" },
            { label: t(MSG.admin.analytics.platformHealth.p95Latency), value: "—", status: "healthy" },
          ].map((item) => (
            <div key={item.label} className="flex items-center justify-between rounded-md border border-[var(--color-border)] p-3">
              <div>
                <p className="text-xs text-muted-foreground">{item.label}</p>
                <p className="text-sm font-medium text-white">{item.value}</p>
              </div>
              <span className={`h-2 w-2 rounded-full ${item.status === "healthy" ? "bg-[var(--color-success)]/40" : "bg-[var(--color-destructive)]/40"}`} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
